import { DateTime, DateTimeFormatOptions } from "luxon";
import i18n from "app/i18n";

type DateFormatPref = "us" | "eu";
type TimeFormatPref = "12h" | "24h";

export const formatTime = (
  dt: DateTime,
  timeFormat: TimeFormatPref = "12h",
): string => {
  if (timeFormat === "24h") {
    return dt.toFormat("HH:mm");
  }
  if (i18n.language?.startsWith("ja")) {
    return `${dt.toFormat("a")}${dt.toFormat("h:mm")}`;
  }
  return dt.toFormat("h:mm a");
};

export const formatMonthDay = (
  date: DateTime | Date,
  dateFormat: DateFormatPref = "us",
): string => {
  const dt = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  const month = dt.toLocaleString(
    { month: "short" },
    { locale: i18n.language },
  );
  const day = dt.day;
  return dateFormat === "eu" ? `${day} ${month}` : `${month} ${day}`;
};

export const formatMonthDayYear = (
  date: DateTime | Date,
  dateFormat: DateFormatPref = "us",
): string => {
  const dt = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  const month = dt.toLocaleString(
    { month: "short" },
    { locale: i18n.language },
  );
  const day = dt.day;
  const year = dt.year;
  return dateFormat === "eu"
    ? `${day} ${month} ${year}`
    : `${month} ${day}, ${year}`;
};

/**
 * "Aug 14" for a date in the current year, "Aug 14, 2027" otherwise — so a
 * far-off expiry or subscription end date can't read as a nearby one.
 */
export const formatMonthDayWithYearIfNeeded = (
  date: DateTime | Date,
  dateFormat: DateFormatPref = "us",
): string => {
  const dt = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  return dt.year === DateTime.now().year
    ? formatMonthDay(dt, dateFormat)
    : formatMonthDayYear(dt, dateFormat);
};

export const formatFullDate = (
  date: DateTime | Date,
  dateFormat: DateFormatPref = "us",
): string => {
  const dt = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  const month = dt.toLocaleString(
    { month: "long" },
    { locale: i18n.language },
  );
  const day = dt.day;
  const year = dt.year;
  return dateFormat === "eu"
    ? `${day} ${month} ${year}`
    : `${month} ${day}, ${year}`;
};

export const formatFullWeekdayDate = (
  date: DateTime | Date,
  dateFormat: DateFormatPref = "us",
): string => {
  const dt = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  const weekday = dt.toLocaleString(
    { weekday: "long" },
    { locale: i18n.language },
  );
  return `${weekday}, ${formatFullDate(dt, dateFormat)}`;
};

export const formatFullWeekdayDateTime = (
  date: DateTime | Date,
  dateFormat: DateFormatPref = "us",
  timeFormat: TimeFormatPref = "12h",
): string => {
  const dt = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  const time = dt.toLocaleString(
    {
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short",
      hour12: timeFormat === "12h",
    },
    { locale: i18n.language },
  );
  return `${formatFullWeekdayDate(dt, dateFormat)} ${i18n.t("at")} ${time}`;
};

export const formatFullDateTime = (
  date: DateTime | Date,
  dateFormat: DateFormatPref = "us",
  timeFormat: TimeFormatPref = "12h",
): string => {
  const dt = date instanceof DateTime ? date : DateTime.fromJSDate(date);
  const time = dt.toLocaleString(
    {
      hour: "numeric",
      minute: "numeric",
      timeZoneName: "short",
      hour12: timeFormat === "12h",
    },
    { locale: i18n.language },
  );
  return `${formatFullDate(dt, dateFormat)} ${i18n.t("at")} ${time}`;
};

export const DATETIME_FULL_WEEKDAY: DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
  weekday: "long",
  hour: "numeric",
  minute: "numeric",
  timeZoneName: "short",
};

export const DATETIME_SHORT_DAY: DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  weekday: undefined,
};

export const calendarDateFormat = (date: DateTime, calendarView: string) => {
  if (calendarView === "timeGridDay") {
    return date.toLocaleString({ month: "long", year: "numeric" });
  }

  if (calendarView === "dayGridMonth") {
    return date.toLocaleString({ month: "long", year: "numeric" });
  }

  if (calendarView === "listWeek") {
    return date.toLocaleString({ month: "long", year: "numeric" });
  }

  if (calendarView === "facilityCalendar") {
    return date.toLocaleString({ month: "long", year: "numeric" });
  }

  // Week-based views: timeGridWeek, timeGridNarrow
  let startOfWeek = date;
  let endOfWeek: DateTime = date.plus({ day: 6 });

  if (calendarView === "timeGridNarrow") {
    if (startOfWeek.weekday !== 7) {
      startOfWeek = startOfWeek.minus({ days: startOfWeek.weekday });
    }

    endOfWeek = startOfWeek.plus({ days: 6 });
  }

  const startMonth = startOfWeek.toLocaleString({ month: "long" });
  const startYear = startOfWeek.toLocaleString({ year: "numeric" });

  let dateLabel: string;

  if (startOfWeek.month === endOfWeek.month) {
    dateLabel = `${startMonth} ${startYear}`;
  } else {
    if (startOfWeek.year === endOfWeek.year) {
      dateLabel = `${startOfWeek.toLocaleString({
        month: "short",
      })} - ${endOfWeek.toLocaleString({ month: "short", year: "numeric" })}`;
    } else {
      dateLabel = `${startOfWeek.toLocaleString({
        month: "short",
        year: "numeric",
      })} - ${endOfWeek.toLocaleString({ month: "short", year: "numeric" })}`;
    }
  }

  return dateLabel;
};

/** @deprecated Use calendarDateFormat instead */
export const weekFormat = calendarDateFormat;

export const formatDate = (dateString: string): string => {
  const date = DateTime.fromISO(dateString);

  if (!date.isValid) {
    return "Invalid Date";
  }

  const datePart = date.toLocaleString({
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const timePart = date.toLocaleString({
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} • ${timePart}`;
};
